/**
 * THROWAWAY DEMO CODE (Pillar procurement demo).
 *
 * Decides which item a typed message is, among items that each have a few
 * phrasings (a page's questions, an agreement's jobs): exact match after
 * normalisation, else the closest item whose content words overlap enough
 * (Jaccard).
 */
import { contentTerms, tokenize } from './local-retrieval';

const MIN_JACCARD = 0.6;

function normalize(text: string): string {
  return tokenize(text).join(' ');
}

function jaccard(a: ReadonlySet<string>, b: ReadonlySet<string>): number {
  if (a.size === 0 || b.size === 0) return 0;
  let shared = 0;
  for (const term of a) {
    if (b.has(term)) shared += 1;
  }
  return shared / (a.size + b.size - shared);
}

/** The item whose phrasing the text is, or undefined when none is close enough. */
export function matchPhrasing<T>(
  text: string,
  items: T[],
  phrasingsOf: (item: T) => string[],
): T | undefined {
  const normalized = normalize(text);
  if (normalized.length === 0) return undefined;

  const exact = items.find((item) =>
    phrasingsOf(item).some((phrasing) => normalize(phrasing) === normalized),
  );
  if (exact) return exact;

  const textTerms = new Set(contentTerms(text));
  let best: T | undefined;
  let bestScore = 0;
  for (const item of items) {
    for (const phrasing of phrasingsOf(item)) {
      const score = jaccard(textTerms, new Set(contentTerms(phrasing)));
      if (score > bestScore) {
        bestScore = score;
        best = item;
      }
    }
  }
  return bestScore >= MIN_JACCARD ? best : undefined;
}
